import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import { Sport, TrainingType } from "../types/activity";
import { PerformanceType } from "../types/post";
import { CustomTrainingType } from "../types/user";

/** Catalogue training type name for a performance intent (null for Testing). */
export function performanceTrainingTypeName(
  intent: PerformanceType | undefined,
): string | null {
  if (intent === "competition") return "Competition";
  if (intent === "gameDay") return "Game Day";
  return null;
}

// Module-level caches — both collections are small and effectively static per
// session, so one fetch per page load is plenty.
let sportsCache: Sport[] | null = null;
let trainingTypesCache: TrainingType[] | null = null;

export class TrainingTypeService {
  /**
   * Fetch all sports from the sports collection, sorted by name.
   */
  static async fetchSports(): Promise<Sport[]> {
    if (sportsCache) return sportsCache;
    try {
      const snap = await getDocs(collection(db, "sports"));
      const sports = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<Sport, "id">),
      }));
      sports.sort((a, b) => a.name.localeCompare(b.name));
      sportsCache = sports;
      return sports;
    } catch (error) {
      console.error("Error fetching sports:", error);
      throw new Error("Failed to fetch sports");
    }
  }

  /**
   * Fetch all training types from the trainingTypes collection, sorted by name.
   */
  static async fetchTrainingTypes(): Promise<TrainingType[]> {
    if (trainingTypesCache) return trainingTypesCache;
    try {
      const snap = await getDocs(collection(db, "trainingTypes"));
      const trainingTypes = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<TrainingType, "id">),
      }));
      trainingTypes.sort((a, b) => a.name.localeCompare(b.name));
      trainingTypesCache = trainingTypes;
      return trainingTypes;
    } catch (error) {
      console.error("Error fetching training types:", error);
      throw new Error("Failed to fetch training types");
    }
  }

  /**
   * Fetch the training types linked to the given sports (via each sport's
   * trainingTypeIDs). When no sport IDs are provided, returns all training types.
   */
  static async fetchTrainingTypesForSports(
    trainingSportIDs?: string[],
    customTrainingTypes?: CustomTrainingType[],
  ): Promise<TrainingType[]> {
    const allTrainingTypes = await this.fetchTrainingTypes();
    let filtered: TrainingType[];
    if (!trainingSportIDs || trainingSportIDs.length === 0) {
      filtered = allTrainingTypes;
    } else {
      const sports = await this.fetchSports();
      const linkedIds = new Set<string>();
      trainingSportIDs.forEach((sportId) => {
        const sport = sports.find((s) => s.id === sportId);
        sport?.trainingTypeIDs?.forEach((id) => linkedIds.add(id));
      });

      // If the selected sports have no linked training types yet, fall back to
      // the full list so the user is never left with an empty picker.
      filtered =
        linkedIds.size === 0
          ? allTrainingTypes
          : allTrainingTypes.filter((t) => t.id && linkedIds.has(t.id));
    }

    return this.mergeCustomTrainingTypes(filtered, customTrainingTypes, allTrainingTypes);
  }

  /**
   * Project user custom training types into the TrainingType shape used by
   * pickers, inheriting the base type's load modifier.
   */
  static mergeCustomTrainingTypes(
    catalogueTypes: TrainingType[],
    customTrainingTypes?: CustomTrainingType[],
    allCatalogueTypes?: TrainingType[],
  ): TrainingType[] {
    if (!customTrainingTypes?.length) return catalogueTypes;
    const lookup = allCatalogueTypes ?? catalogueTypes;
    const customs: TrainingType[] = customTrainingTypes
      .filter((c) => c.id && c.name?.trim())
      .map((c) => {
        const base =
          lookup.find((t) => t.id === c.relatedTrainingTypeID) ??
          lookup.find(
            (t) =>
              t.name.trim().toLowerCase() ===
              c.relatedTrainingTypeName.trim().toLowerCase(),
          );
        return {
          id: c.id,
          name: c.name.trim(),
          modifier: base?.modifier ?? 1,
          description: base?.description ?? "",
          includeGPS: base?.includeGPS ?? false,
          alternativeNames: [],
          sportReferenceList: [],
          isCustomTrainingType: true,
          relatedTrainingTypeID: c.relatedTrainingTypeID,
        };
      });

    const seen = new Set(
      catalogueTypes.map((t) => t.name.trim().toLowerCase()).filter(Boolean),
    );
    const uniqueCustoms = customs.filter((c) => {
      const key = c.name.trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return [...catalogueTypes, ...uniqueCustoms].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }

  /**
   * Get a single training type by its document ID (uses cache when warm).
   */
  static async getTrainingTypeById(
    trainingTypeID: string,
  ): Promise<TrainingType | null> {
    if (!trainingTypeID) return null;
    try {
      if (trainingTypesCache) {
        return trainingTypesCache.find((t) => t.id === trainingTypeID) ?? null;
      }
      const snap = await getDoc(doc(db, "trainingTypes", trainingTypeID));
      if (!snap.exists()) return null;
      return { id: snap.id, ...(snap.data() as Omit<TrainingType, "id">) };
    } catch (error) {
      console.error("Error fetching training type by ID:", error);
      return null;
    }
  }

  /**
   * Resolve a legacy tag / name to a training type by matching the name and
   * alternativeNames (case-insensitive). Used for backward compatibility with
   * logs and sessions that predate trainingTypeID.
   */
  static async findTrainingTypeByName(
    name: string,
  ): Promise<TrainingType | null> {
    const trimmed = name?.trim().toLowerCase();
    if (!trimmed) return null;
    const allTrainingTypes = await this.fetchTrainingTypes();
    return (
      allTrainingTypes.find(
        (t) =>
          t.name.trim().toLowerCase() === trimmed ||
          t.alternativeNames?.some(
            (alt) => alt.trim().toLowerCase() === trimmed,
          ),
      ) ?? null
    );
  }

  /**
   * Resolve the catalogue training type for a performance intent by name
   * (PerformanceLog.md §1.4): "Competition" / "Game Day". Returns null for
   * Testing (athlete-chosen) or when the catalogue has no such entry, in
   * which case callers leave `trainingTypeID` unset and still write the tag.
   */
  static async resolvePerformanceTrainingType(
    intent: PerformanceType | undefined,
  ): Promise<TrainingType | null> {
    const name = performanceTrainingTypeName(intent);
    if (!name) return null;
    try {
      return await this.findTrainingTypeByName(name);
    } catch (error) {
      console.error("Error resolving performance training type:", error);
      return null;
    }
  }

  /**
   * Resolve a Session/SessionTemplate to a training type, preferring the new
   * trainingTypeID and falling back to parsing the legacy composite
   * sessionTypeID (`sportId:Name` or `sportid-name`). Synchronous so it can be
   * used in render loops with an already-fetched trainingTypes list.
   */
  static resolveTrainingTypeForSession(
    entity: { trainingTypeID?: string; sessionTypeID?: string },
    trainingTypes: TrainingType[],
  ): TrainingType | null {
    if (entity.trainingTypeID) {
      const match = trainingTypes.find((t) => t.id === entity.trainingTypeID);
      if (match) return match;
    }

    const legacyID = entity.sessionTypeID?.trim();
    if (!legacyID) return null;

    const normalize = (value: string) =>
      value.toLowerCase().replace(/[^a-z0-9]/g, "");

    const matchesName = (t: TrainingType, normalized: string) =>
      normalize(t.name) === normalized ||
      t.alternativeNames?.some((alt) => normalize(alt) === normalized);

    // `sportId:Name` format — the name is everything after the first colon.
    const colonIndex = legacyID.indexOf(":");
    if (colonIndex >= 0) {
      const normalizedName = normalize(legacyID.slice(colonIndex + 1));
      return (
        trainingTypes.find((t) => matchesName(t, normalizedName)) ?? null
      );
    }

    // `sportid-name` slug format — the sport/name boundary is ambiguous, so
    // try progressively shorter dash-delimited suffixes (longest first).
    const parts = legacyID.split("-");
    for (let i = 1; i < parts.length; i++) {
      const normalizedSuffix = normalize(parts.slice(i).join("-"));
      if (!normalizedSuffix) continue;
      const match = trainingTypes.find((t) =>
        matchesName(t, normalizedSuffix),
      );
      if (match) return match;
    }
    return null;
  }

  /**
   * Display name for a Session/SessionTemplate: resolved training type name,
   * or null when nothing matches.
   */
  static getSessionTrainingTypeName(
    entity: { trainingTypeID?: string; sessionTypeID?: string },
    trainingTypes: TrainingType[],
  ): string | null {
    return (
      this.resolveTrainingTypeForSession(entity, trainingTypes)?.name ?? null
    );
  }

  /** Clear the module caches (e.g. after admin edits in the same session). */
  static clearCache(): void {
    sportsCache = null;
    trainingTypesCache = null;
  }
}

export const trainingTypeService = TrainingTypeService;
